import {
  type CatalogueItemResource,
  CatalogueItemsController_read_v1,
  type OutletResource,
  OutletsController_read_v1,
  type SaleLineResource,
} from '@repo/api-client';
import { useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';

/**
 * Which outlet sold it, by id. `GET /v1/outlets/{id}` is `@RequireRole()` +
 * `@RequireOutletScope()`, so it is bounded by the same reach the sale read was —
 * an outlet whose sale this caller could read is an outlet they can name. A
 * failure costs a name and never the screen.
 */
export function useNamedOutlet(outletId: string | undefined): OutletResource | null {
  const client = useApiClient();
  const [outlet, setOutlet] = useState<OutletResource | null>(null);

  useEffect(() => {
    if (outletId === undefined) {
      setOutlet(null);

      return;
    }

    const controller = new AbortController();
    let live = true;

    client
      .request(OutletsController_read_v1, { path: { id: outletId }, signal: controller.signal })
      .then((answer) => {
        if (live) setOutlet(answer);
      })
      .catch(() => {
        if (live) setOutlet(null);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId]);

  return outlet;
}

/**
 * What was in the basket, by name — as many names as could be read, keyed by
 * catalogue item id.
 *
 * **One read per *distinct* item, not one per line.** A basket of six bottles of
 * water is one line, but a basket that rang the same item up twice at two prices
 * is two lines naming one item (FR-417), and asking for it twice would be two
 * requests for one answer.
 *
 * `GET /v1/catalogue/items/{id}` is `@RequireRole()` + `@RequireOutletScope()`, so
 * a manager reads it (FR-232), and a **retired** item still answers — which
 * matters here more than anywhere: a sale outlives the item it was sold against,
 * and `itemRetiredAtSale` says some of these baskets contain exactly that.
 *
 * Reading the catalogue whole was the alternative and is the wrong one:
 * `docs/ui-conventions.md` puts the catalogue in case 2, the sets that grow with
 * *use* and must be paged, so `collectPages` over it would get slower every month
 * to name three items.
 *
 * **A failure costs a name and never the screen**, and it is per item: one item
 * that could not be read leaves its line showing an id while the rest of the
 * basket reads normally.
 */
export function useBasketItems(
  // Only the item id is read, so an imported machine bill's lines name their
  // items through this too.
  lines: readonly Pick<SaleLineResource, 'catalogueItemId'>[] | undefined,
): ReadonlyMap<string, CatalogueItemResource> {
  const client = useApiClient();
  const [items, setItems] = useState<ReadonlyMap<string, CatalogueItemResource>>(new Map());

  // Derived from `lines` rather than from the sale, so the effect below does not
  // re-run when an unrelated field of the record changes identity.
  const itemIds = useMemo(
    () => [...new Set((lines ?? []).map((line) => line.catalogueItemId))],
    [lines],
  );

  useEffect(() => {
    if (itemIds.length === 0) {
      setItems(new Map());

      return;
    }

    const controller = new AbortController();
    let live = true;

    // `allSettled` and not `all`: one item that cannot be read must not take the
    // other names down with it. The reads go out together — a basket is small and
    // bounded by what one customer carried to a counter, so this is a handful of
    // requests rather than a page of them.
    void Promise.allSettled(
      itemIds.map((itemId) =>
        client.request(CatalogueItemsController_read_v1, {
          path: { id: itemId },
          signal: controller.signal,
        }),
      ),
    ).then((answers) => {
      if (!live) return;

      const named = new Map<string, CatalogueItemResource>();

      for (const answer of answers) {
        if (answer.status === 'fulfilled') named.set(answer.value.id, answer.value);
      }

      setItems(named);
    });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, itemIds]);

  return items;
}
